import { zodResolver } from '@hookform/resolvers/zod';
import { lignesSansTva, messageLignesSansTva } from '@imp/shared/controle-saisie';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Plus,
  Printer,
  Receipt,
  Send,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatEuro, listArticles, listTauxTva, listUnites } from '@/modules/catalogue/api';
import { ChoixChantier } from '@/modules/chantiers/ChoixChantier';
import { listChantiers } from '@/modules/chantiers/api';
import { ChoixClient } from '@/modules/clients/ChoixClient';
import { listContacts } from '@/modules/clients/api';
import {
  annulerDecisionDevis,
  computeDevisTotals,
  createDevis,
  DEVIS_STATUT_LABELS,
  getDevis,
  markDevisRelance,
  nextDevisNumber,
  setDevisStatut,
  updateDevis,
} from '@/modules/devis/api';
import { createFactureFromDevis } from '@/modules/factures/api';

const ligneSchema = z.object({
  article_id: z.string(),
  libelle: z.string().min(1, 'Désignation requise'),
  description: z.string(),
  quantite: z.coerce.string(),
  unite_id: z.string(),
  prix_unitaire_ht: z.coerce.string(),
  remise_pourcentage: z.coerce.string(),
  taux_tva_id: z.string(),
});

const schema = z.object({
  contact_id: z.string().min(1, 'Client obligatoire'),
  // Sans chantier, le chiffre d'affaires ne se rattache à rien : ni marge, ni
  // suivi, ni pilotage. On l'exige à la source plutôt que de le rattraper après.
  chantier_id: z.string().min(1, 'Chantier obligatoire'),
  objet: z.string().min(1, 'Objet obligatoire'),
  date_emission: z.string(),
  date_expiration: z.string(),
  statut: z.string(),
  lignes: z.array(ligneSchema).min(1, 'Ajoutez au moins une ligne'),
});

type FormValues = z.infer<typeof schema>;

/**
 * Le chemin d'un champ de ligne, tel que react-hook-form l'attend
 * (« lignes.3.libelle »). Le construire ici une seule fois évite de le
 * réécrire à chaque champ, et garde le typage exact de celui-ci.
 */
type ChampLigne = keyof z.infer<typeof ligneSchema>;
const champLigne = <N extends ChampLigne>(i: number, nom: N): `lignes.${number}.${N}` =>
  `lignes.${String(i)}.${nom}` as `lignes.${number}.${N}`;

const EMPTY_LIGNE = {
  article_id: '',
  libelle: '',
  description: '',
  quantite: '1',
  unite_id: '',
  prix_unitaire_ht: '',
  remise_pourcentage: '0',
  taux_tva_id: '',
};

function isoDay(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

export function DevisFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });
  const articlesQ = useQuery({ queryKey: ['articles'], queryFn: listArticles });
  const unitesQ = useQuery({ queryKey: ['unites'], queryFn: listUnites });
  const tvaQ = useQuery({ queryKey: ['taux_tva'], queryFn: listTauxTva });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      contact_id: '',
      chantier_id: '',
      objet: '',
      date_emission: isoDay(0),
      date_expiration: isoDay(30),
      statut: 'brouillon',
      lignes: [EMPTY_LIGNE],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lignes' });

  const editQuery = useQuery({
    queryKey: ['devis', id],
    queryFn: () => getDevis(id as string),
    enabled: isEdit,
  });

  useEffect(() => {
    const d = editQuery.data;
    if (!d) return;
    reset({
      contact_id: d.contact_id,
      chantier_id: d.chantier_id ?? '',
      objet: d.objet,
      date_emission: d.date_emission,
      date_expiration: d.date_expiration,
      statut: d.statut,
      lignes:
        d.devis_lignes.length > 0
          ? d.devis_lignes
              .slice()
              .sort((a, b) => a.ordre - b.ordre)
              .map((l) => ({
                article_id: l.article_id ?? '',
                libelle: l.libelle,
                description: l.description ?? '',
                quantite: l.quantite,
                unite_id: l.unite_id ?? '',
                prix_unitaire_ht: l.prix_unitaire_ht,
                remise_pourcentage: l.remise_pourcentage,
                taux_tva_id: l.taux_tva_id ?? '',
              }))
          : [EMPTY_LIGNE],
    });
  }, [editQuery.data, reset]);

  const articleMap = new Map((articlesQ.data ?? []).map((a) => [a.id, a]));
  const tvaList = tvaQ.data ?? [];

  const watchedLignes = watch('lignes');
  const totals = computeDevisTotals(watchedLignes, tvaList);

  // Une ligne sans TVA (ou à 0 %) rend le devis faux : il peut rester en
  // brouillon, mais ne part pas, n'est pas accepté et ne devient pas facture.
  // On contrôle les lignes à l'écran ET celles enregistrées, car les boutons du
  // suivi agissent sur ce qui est enregistré.
  const tauxParId = new Map(tvaList.map((t) => [t.id, Number(t.taux)]));
  const tauxDe = (tauxId: string | null): number | null =>
    tauxId ? (tauxParId.get(tauxId) ?? null) : null;
  const sansTva = tvaQ.isSuccess
    ? lignesSansTva(
        watchedLignes.map((l, i) => ({
          totalHt: totals.ligneTotals[i]?.total_ht ?? 0,
          taux: tauxDe(l.taux_tva_id),
        })),
      )
    : [];
  const sansTvaEnregistre =
    tvaQ.isSuccess && editQuery.data
      ? lignesSansTva(
          editQuery.data.devis_lignes
            .slice()
            .sort((a, b) => a.ordre - b.ordre)
            .map((l) => ({ totalHt: Number(l.total_ht), taux: tauxDe(l.taux_tva_id) })),
        )
      : [];
  const tvaBloque = sansTva.length > 0 || sansTvaEnregistre.length > 0;
  const [refusTva, setRefusTva] = useState(false);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      const t = computeDevisTotals(v.lignes, tvaList);
      const header = {
        contact_id: v.contact_id,
        chantier_id: v.chantier_id === '' ? null : v.chantier_id,
        objet: v.objet,
        date_emission: v.date_emission,
        date_expiration: v.date_expiration,
        statut: v.statut,
        montant_ht: t.montant_ht.toFixed(2),
        montant_tva: t.montant_tva.toFixed(2),
        montant_ttc: t.montant_ttc.toFixed(2),
        detail_tva: t.detail_tva,
      };
      const lignes = v.lignes.map((l, i) => ({
        ordre: i,
        type: 'article',
        article_id: l.article_id === '' ? null : l.article_id,
        libelle: l.libelle,
        description: l.description.trim() === '' ? null : l.description.trim(),
        quantite: String(Number(l.quantite.replace(',', '.')) || 0),
        unite_id: l.unite_id === '' ? null : l.unite_id,
        prix_unitaire_ht: String(Number(l.prix_unitaire_ht.replace(',', '.')) || 0),
        remise_pourcentage: String(Number(l.remise_pourcentage.replace(',', '.')) || 0),
        taux_tva_id: l.taux_tva_id === '' ? null : l.taux_tva_id,
        total_ht: (t.ligneTotals[i]?.total_ht ?? 0).toFixed(2),
        total_ttc: (t.ligneTotals[i]?.total_ttc ?? 0).toFixed(2),
      }));
      if (isEdit && id) await updateDevis(id, header, lignes);
      else await createDevis(header, lignes, await nextDevisNumber());
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devis'] });
      navigate('/devis');
    },
  });

  const convertMut = useMutation({
    mutationFn: () => createFactureFromDevis(id ?? ''),
    onSuccess: (factureId) => {
      navigate(`/factures/${factureId}`);
    },
  });

  const statutMut = useMutation({
    mutationFn: (statut: string) => setDevisStatut(id ?? '', statut),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devis', id] });
      void queryClient.invalidateQueries({ queryKey: ['devis'] });
    },
  });
  const annulationMut = useMutation({
    mutationFn: () => annulerDecisionDevis(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devis', id] });
      void queryClient.invalidateQueries({ queryKey: ['devis'] });
    },
  });
  const relanceMut = useMutation({
    mutationFn: () => markDevisRelance(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devis', id] });
    },
  });

  const onSubmit = handleSubmit((v) => {
    if (sansTva.length > 0 && v.statut !== 'brouillon' && v.statut !== 'a_envoyer') {
      setRefusTva(true);
      return;
    }
    setRefusTva(false);
    mutation.mutate(v);
  });

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/devis" aria-label="Retour">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEdit ? 'Modifier le devis' : 'Nouveau devis'}
          </h1>
        </div>
        {isEdit && id ? (
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Button asChild variant="outline">
              <Link to={`/devis/${id}/pdf`}>
                <Printer className="h-4 w-4" /> PDF
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={convertMut.isPending || tvaBloque}
              title={tvaBloque ? 'Une ligne n’a pas de TVA : à corriger d’abord' : undefined}
              onClick={() => {
                convertMut.mutate();
              }}
            >
              <Receipt className="h-4 w-4" />
              {convertMut.isPending ? 'Conversion…' : 'Convertir en facture'}
            </Button>
          </div>
        ) : null}
      </div>

      {isEdit && editQuery.data ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <span className="text-sm text-muted-foreground">Suivi :</span>
            <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
              {DEVIS_STATUT_LABELS[editQuery.data.statut] ?? editQuery.data.statut}
            </span>
            {editQuery.data.statut === 'brouillon' || editQuery.data.statut === 'a_envoyer' ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={statutMut.isPending || tvaBloque}
                onClick={() => {
                  statutMut.mutate('envoye');
                }}
              >
                <Send className="h-4 w-4" /> Marquer envoyé
              </Button>
            ) : null}
            {editQuery.data.statut === 'envoye' || editQuery.data.statut === 'consulte' ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={statutMut.isPending || tvaBloque}
                  onClick={() => {
                    statutMut.mutate('signe');
                  }}
                >
                  <Check className="h-4 w-4" /> Accepté
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={statutMut.isPending}
                  onClick={() => {
                    statutMut.mutate('refuse');
                  }}
                >
                  <X className="h-4 w-4" /> Refusé
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={relanceMut.isPending}
                  onClick={() => {
                    relanceMut.mutate();
                  }}
                >
                  Relancer le client
                </Button>
              </>
            ) : null}
            {editQuery.data.statut === 'signe' || editQuery.data.statut === 'refuse' ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={annulationMut.isPending}
                onClick={() => {
                  const quoi = editQuery.data.statut === 'signe' ? 'l’acceptation' : 'le refus';
                  if (window.confirm(`Annuler ${quoi} ? Le devis repassera « Envoyé ».`)) {
                    annulationMut.mutate();
                  }
                }}
              >
                <Undo2 className="h-4 w-4" />
                {editQuery.data.statut === 'signe' ? 'Annuler l’acceptation' : 'Annuler le refus'}
              </Button>
            ) : null}
            {annulationMut.isError ? (
              <p className="w-full text-sm text-destructive">
                {messageErreur(annulationMut.error)}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {mutation.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Échec : {messageErreur(mutation.error)}
        </div>
      ) : null}

      {sansTva.length > 0 || sansTvaEnregistre.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive"
        >
          {messageLignesSansTva(sansTva.length > 0 ? sansTva : sansTvaEnregistre)}
          {sansTva.length === 0 ? ' Enregistrez le devis corrigé.' : null}
          {refusTva && sansTva.length > 0
            ? ' Pour l’enregistrer en attendant, remettez-le en « Brouillon ».'
            : null}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>En-tête</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="contact_id">Client *</Label>
            <ChoixClient
              id="contact_id"
              clients={contactsQ.data ?? []}
              chargement={contactsQ.isLoading}
              value={watch('contact_id')}
              invalide={Boolean(errors.contact_id)}
              onChange={(v) => {
                setValue('contact_id', v, { shouldValidate: true, shouldDirty: true });
              }}
            />
            {errors.contact_id ? (
              <p className="text-xs text-destructive">{errors.contact_id.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chantier_id">Chantier *</Label>
            <ChoixChantier
              id="chantier_id"
              chantiers={chantiersQ.data ?? []}
              clients={contactsQ.data ?? []}
              chargement={chantiersQ.isLoading}
              value={watch('chantier_id')}
              invalide={Boolean(errors.chantier_id)}
              onChange={(v) => {
                setValue('chantier_id', v, { shouldValidate: true, shouldDirty: true });
              }}
            />
            {errors.chantier_id ? (
              <p className="text-xs text-destructive">
                {errors.chantier_id.message} —{' '}
                <Link to="/chantiers/nouveau" className="underline">
                  créer le chantier
                </Link>
              </p>
            ) : (
              <p className="text-muted-foreground text-xs">
                Le chiffre d&apos;affaires de ce devis sera rattaché à ce chantier.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="statut">Statut</Label>
            <Select id="statut" {...register('statut')}>
              {Object.entries(DEVIS_STATUT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="objet">Objet *</Label>
            <Input id="objet" placeholder="Ex : Réfection de toiture" {...register('objet')} />
            {errors.objet ? <p className="text-xs text-destructive">{errors.objet.message}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="date_emission">Date d'émission</Label>
            <Input id="date_emission" type="date" {...register('date_emission')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="date_expiration">Valable jusqu'au</Label>
            <Input id="date_expiration" type="date" {...register('date_expiration')} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lignes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {fields.map((field, i) => (
            <div key={field.id} className="space-y-2 rounded-lg border p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <Input placeholder="Désignation *" {...register(champLigne(i, 'libelle'))} />
                  {errors.lignes?.[i]?.libelle ? (
                    <p className="mt-1 text-xs text-destructive">
                      {errors.lignes[i]?.libelle?.message}
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Supprimer la ligne"
                  onClick={() => {
                    remove(i);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                rows={2}
                placeholder="Description (facultative) — s'imprime sous la désignation"
                {...register(champLigne(i, 'description'))}
              />
              <div className="grid gap-2 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <span className="mb-1 block text-xs text-muted-foreground">Depuis le catalogue</span>
                  {(() => {
                    const reg = register(champLigne(i, 'article_id'));
                    return (
                      <Select
                        {...reg}
                        onChange={(e) => {
                          void reg.onChange(e);
                          const a = articleMap.get(e.target.value);
                          if (a) {
                            setValue(champLigne(i, 'libelle'), a.libelle, { shouldDirty: true });
                            // Le descriptif du catalogue suit l'article : c'est
                            // ce que le client lit sous la désignation.
                            setValue(champLigne(i, 'description'), a.description ?? '', {
                              shouldDirty: true,
                            });
                            setValue(champLigne(i, 'prix_unitaire_ht'), a.prix_vente_ht, {
                              shouldDirty: true,
                            });
                            setValue(champLigne(i, 'unite_id'), a.unite_id, { shouldDirty: true });
                            setValue(champLigne(i, 'taux_tva_id'), a.taux_tva_id, {
                              shouldDirty: true,
                            });
                          }
                        }}
                      >
                        <option value="">— Libre —</option>
                        {(articlesQ.data ?? []).map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.libelle}
                          </option>
                        ))}
                      </Select>
                    );
                  })()}
                </div>
                <div>
                  <span className="mb-1 block text-xs text-muted-foreground">Qté</span>
                  <Input inputMode="decimal" {...register(champLigne(i, 'quantite'))} />
                </div>
                <div>
                  <span className="mb-1 block text-xs text-muted-foreground">Unité</span>
                  <Select {...register(champLigne(i, 'unite_id'))}>
                    <option value="">—</option>
                    {(unitesQ.data ?? []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.code}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <span className="mb-1 block text-xs text-muted-foreground">PU HT</span>
                  <Input inputMode="decimal" {...register(champLigne(i, 'prix_unitaire_ht'))} />
                </div>
                <div>
                  <span className="mb-1 block text-xs text-muted-foreground">TVA</span>
                  <Select
                    aria-invalid={sansTva.includes(i + 1)}
                    className={sansTva.includes(i + 1) ? 'border-destructive' : undefined}
                    {...register(champLigne(i, 'taux_tva_id'))}
                  >
                    <option value="">—</option>
                    {tvaList.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="text-right text-sm text-muted-foreground">
                Total ligne HT : {formatEuro(String(totals.ligneTotals[i]?.total_ht ?? 0))}
              </div>
            </div>
          ))}

          {errors.lignes?.message ? (
            <p className="text-xs text-destructive">{errors.lignes.message}</p>
          ) : null}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              append(EMPTY_LIGNE);
            }}
          >
            <Plus className="h-4 w-4" /> Ajouter une ligne
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="ml-auto max-w-xs space-y-1 py-4 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total HT</span>
            <span className="font-medium">{formatEuro(String(totals.montant_ht))}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">TVA</span>
            <span className="font-medium">{formatEuro(String(totals.montant_tva))}</span>
          </div>
          <div className="flex justify-between border-t pt-1 text-base">
            <span className="font-semibold">Total TTC</span>
            <span className="font-bold">{formatEuro(String(totals.montant_ttc))}</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le devis'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/devis">Annuler</Link>
        </Button>
        {Object.keys(errors).length > 0 ? (
          <span className="text-sm text-destructive">
            Veuillez corriger les champs signalés en rouge.
          </span>
        ) : null}
      </div>
    </form>
  );
}
